import type { DiskFileKind } from '../Contracts/DiskNode'

/**
 * Як переглядач показує файл (`DISK-001` §4.7):
 *
 * - `image` — `<img>` з масштабом і поворотом;
 * - `pdf` — pdf.js з шаром тексту;
 * - `video` / `audio` — нативні `<video>` / `<audio>`;
 * - `text` / `csv` / `json` — вбудований переглядач до {@link DISK_TEXT_PREVIEW_MAX_BYTES};
 * - `tooLarge` — текстовий файл, більший за межу: лише «Скачати»;
 * - `office` — документи Office/ODF/RTF: Collabora в режимі перегляду
 *   (`edit_session` диска; редагування — фаза 3);
 * - `archive` — ZIP: перелік записів без розпакування (`…/archive`);
 * - `dangerous` — HTML, SVG, XML, JavaScript: бек віддає їх лише `attachment`,
 *   у переглядачі вони не рендеряться — лише «Скачати»;
 * - `unsupported` — решта (інші архіви, бінарні файли).
 */
export type DiskViewerMode =
    | 'image'
    | 'pdf'
    | 'video'
    | 'audio'
    | 'text'
    | 'csv'
    | 'json'
    | 'tooLarge'
    | 'office'
    | 'archive'
    | 'dangerous'
    | 'unsupported'

export interface DiskViewerFile {
    name: string
    mime: string | null
    kind: DiskFileKind
    size: number
}

/** Межа вбудованого перегляду тексту, CSV, JSON і коду — 2 МБ (`DISK-001` §4.7). */
export const DISK_TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024

/**
 * MIME, які браузер виконав би як активний вміст, — дзеркало
 * `DiskContentResponder::DANGEROUS_TYPES` беку (плюс будь-який `…+xml`).
 */
const DANGEROUS_MIMES = new Set([
    'text/html',
    'application/xhtml+xml',
    'image/svg+xml',
    'text/xml',
    'application/xml',
    'text/javascript',
    'application/javascript',
    'application/x-javascript',
    'application/ecmascript',
    'text/ecmascript',
    'application/x-httpd-php',
    'text/x-php',
    'application/xslt+xml',
    'text/xsl',
])

/** Розширення небезпечних типів — дзеркало `DiskContentResponder::DANGEROUS_EXTENSIONS`. */
const DANGEROUS_EXTENSIONS = new Set(['html', 'htm', 'xhtml', 'shtml', 'svg', 'svgz', 'xml', 'xsl', 'xslt', 'js', 'mjs'])

const IMAGE_MIMES = new Set(['image/jpeg', 'image/pjpeg', 'image/png', 'image/webp', 'image/gif'])
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'jpe', 'png', 'webp', 'gif'])

const VIDEO_MIMES = new Set(['video/mp4', 'video/webm', 'video/ogg'])
const VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'webm', 'ogv'])

const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'oga', 'opus', 'm4a', 'aac', 'flac', 'weba'])

/** Формати Collabora — дзеркало `CreateDiskEditSessionAction::OFFICE_EXTENSIONS` беку (інше бек відхилить 422). */
const OFFICE_EXTENSIONS = new Set([
    'doc',
    'docx',
    'docm',
    'dot',
    'dotx',
    'rtf',
    'odt',
    'ott',
    'xls',
    'xlsx',
    'xlsm',
    'xlsb',
    'ods',
    'ots',
    'ppt',
    'pptx',
    'pptm',
    'pps',
    'ppsx',
    'odp',
    'otp',
    'odg',
])

/** ZIP — за розширенням або MIME (офісні документи теж zip, але їх ловить перевірка вище). */
const ZIP_MIMES = new Set(['application/zip', 'application/x-zip-compressed', 'application/x-zip'])

const CSV_EXTENSIONS = new Set(['csv', 'tsv'])
const JSON_EXTENSIONS = new Set(['json', 'geojson', 'webmanifest'])

/** Текст, Markdown і код — показуємо моноширинним як є. */
const TEXT_EXTENSIONS = new Set([
    'txt',
    'text',
    'log',
    'md',
    'markdown',
    'ini',
    'cfg',
    'conf',
    'env',
    'yml',
    'yaml',
    'toml',
    'sql',
    'sh',
    'bash',
    'ts',
    'tsx',
    'jsx',
    'css',
    'scss',
    'less',
    'php',
    'py',
    'rb',
    'go',
    'rs',
    'java',
    'kt',
    'c',
    'h',
    'cpp',
    'hpp',
    'cs',
    'swift',
    'vue',
    'graphql',
    'properties',
    'gitignore',
    'dockerfile',
])

const TEXT_MIMES = new Set(['application/x-yaml', 'application/yaml', 'application/sql', 'application/x-sh'])

/** Розширення назви в нижньому регістрі без крапки; без розширення — порожній рядок. */
export const extensionOf = (name: string): string => {
    const dot = name.lastIndexOf('.')

    return dot >= 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : ''
}

/** MIME без параметрів і в нижньому регістрі: `text/plain; charset=utf-8` → `text/plain`. */
const baseMime = (mime: string | null): string => (mime ?? '').split(';')[0]?.trim().toLowerCase() ?? ''

/** Небезпечний тип — та сама перевірка, що `DiskContentResponder::isDangerous` беку. */
export const isDangerousFile = (mime: string | null, name: string): boolean => {
    const type = baseMime(mime)

    return DANGEROUS_MIMES.has(type) || type.endsWith('+xml') || DANGEROUS_EXTENSIONS.has(extensionOf(name))
}

/** Текстовий режим без урахування розміру — або `null`, якщо файл не текстовий. */
const textMode = (type: string, extension: string, kind: DiskFileKind): 'text' | 'csv' | 'json' | null => {
    if (CSV_EXTENSIONS.has(extension) || type === 'text/csv' || type === 'text/tab-separated-values') return 'csv'
    if (JSON_EXTENSIONS.has(extension) || type === 'application/json' || type.endsWith('+json')) return 'json'
    if (TEXT_EXTENSIONS.has(extension) || TEXT_MIMES.has(type) || type.startsWith('text/') || kind === 'text') {
        return 'text'
    }

    return null
}

/**
 * Режим перегляду за MIME (визначений беком за вмістом) і розширенням назви.
 * Розширення важить там, де вміст не відрізнити: CSV, JSON і код бек часто
 * бачить як `text/plain`, а Office-документ — як `application/zip`.
 */
export const diskViewerMode = (file: DiskViewerFile): DiskViewerMode => {
    const type = baseMime(file.mime)
    const extension = extensionOf(file.name)

    if (isDangerousFile(file.mime, file.name)) return 'dangerous'

    if (IMAGE_MIMES.has(type) || (type === '' && IMAGE_EXTENSIONS.has(extension))) return 'image'
    if (type === 'application/pdf' || (type === '' && extension === 'pdf') || file.kind === 'pdf') return 'pdf'
    if (VIDEO_MIMES.has(type) || (!type.startsWith('audio/') && VIDEO_EXTENSIONS.has(extension))) return 'video'
    if (type.startsWith('audio/') || AUDIO_EXTENSIONS.has(extension) || file.kind === 'audio') return 'audio'

    if (
        OFFICE_EXTENSIONS.has(extension) ||
        file.kind === 'document' ||
        file.kind === 'spreadsheet' ||
        file.kind === 'presentation'
    ) {
        return 'office'
    }

    if (extension === 'zip' || ZIP_MIMES.has(type)) return 'archive'

    const text = textMode(type, extension, file.kind)

    if (text !== null) return file.size > DISK_TEXT_PREVIEW_MAX_BYTES ? 'tooLarge' : text

    return 'unsupported'
}

/**
 * Режими, вміст яких браузер може відкрити сам у новій вкладці (`inline`).
 * Небезпечне бек усе одно віддасть `attachment`, тож кнопку для нього не показуємо.
 */
export const canOpenInNewTab = (mode: DiskViewerMode): boolean =>
    mode !== 'dangerous' && mode !== 'office' && mode !== 'archive' && mode !== 'unsupported'
